import { SupabaseClient } from "@supabase/supabase-js";
import { logAndThrow } from "../utils/logging";
import { MissionOrderCounter, OfficialType } from "../types";

/**
 * Get mission order counters for a league + season
 */
export async function getMissionOrderCounters(
  supabase: SupabaseClient,
  leagueId: string,
  season: string
): Promise<MissionOrderCounter[]> {
  const { data, error } = await supabase
    .from("mission_order_counters")
    .select("*")
    .eq("league_id", leagueId)
    .eq("season", season)
    .order("official_type", { ascending: true });

  if (error) {
    return logAndThrow("fetch mission order counters", error, { leagueId, season });
  }

  return (data || []).map((counter: any) => ({
    id: counter.id,
    league_id: counter.league_id,
    official_type: counter.official_type as OfficialType,
    current_number: counter.current_number,
    last_updated: counter.last_updated,
    created_at: counter.created_at
  }));
}

/**
 * Get a specific counter for league + official type + season
 */
export async function getMissionOrderCounter(
  supabase: SupabaseClient,
  leagueId: string,
  officialType: OfficialType,
  season: string
): Promise<MissionOrderCounter | null> {
  const { data, error } = await supabase
    .from("mission_order_counters")
    .select("*")
    .eq("league_id", leagueId)
    .eq("official_type", officialType)
    .eq("season", season)
    .maybeSingle();

  if (error) {
    return logAndThrow("fetch mission order counter", error, { leagueId, officialType, season });
  }

  if (!data) {
    return null;
  }

  return {
    id: data.id,
    league_id: data.league_id,
    official_type: data.official_type as OfficialType,
    current_number: data.current_number,
    last_updated: data.last_updated,
    created_at: data.created_at
  };
}

/**
 * Set mission order counter value manually
 * Uses RPC function which validates permissions and prevents setting below existing max
 */
export async function setMissionOrderCounter(
  supabase: SupabaseClient,
  leagueId: string,
  officialType: OfficialType,
  newValue: number,
  season: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc("set_mission_order_counter", {
    p_league_id: leagueId,
    p_official_type: officialType,
    p_new_value: newValue,
    p_season: season
  });

  if (error) {
    return logAndThrow("set mission order counter", error, {
      leagueId,
      officialType,
      newValue,
      season
    });
  }

  return data === true;
}

/**
 * Get the next mission order number (preview without incrementing)
 * Useful for showing users what the next number will be
 */
export async function getNextMissionOrderNumber(
  supabase: SupabaseClient,
  leagueId: string,
  officialType: OfficialType,
  season: string
): Promise<{ number: number; display: string }> {
  // First get the current counter for this season
  const counter = await getMissionOrderCounter(supabase, leagueId, officialType, season);
  const currentNumber = counter?.current_number || 0;
  const nextNumber = currentNumber + 1;

  // Format display number with prefix
  const prefix = officialType === 'referee' ? 'A' : 'D';
  const display = `${prefix}-${nextNumber.toString().padStart(3, '0')}`;

  return { number: nextNumber, display };
}

/**
 * Get the highest order number for a league + official type from existing orders
 * Useful for validating counter values
 */
export async function getMaxOrderNumber(
  supabase: SupabaseClient,
  leagueId: string,
  officialType: OfficialType,
  season: string
): Promise<number> {
  const { data, error } = await supabase
    .from("mission_orders")
    .select("order_number")
    .eq("league_id", leagueId)
    .eq("official_type", officialType)
    .eq("season", season)
    .order("order_number", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    return logAndThrow("fetch max order number", error, { leagueId, officialType, season });
  }

  return data?.order_number || 0;
}

/**
 * Initialize counters for a league (creates both referee and delegate counters)
 * Useful when setting up a new league
 */
export async function initializeMissionOrderCounters(
  supabase: SupabaseClient,
  leagueId: string,
  refereeStartNumber: number = 0,
  delegateStartNumber: number = 0
): Promise<void> {
  // Use the RPC function to set both counters
  const [refereeResult, delegateResult] = await Promise.all([
    supabase.rpc("set_mission_order_counter", {
      p_league_id: leagueId,
      p_official_type: "referee",
      p_new_value: refereeStartNumber
    }),
    supabase.rpc("set_mission_order_counter", {
      p_league_id: leagueId,
      p_official_type: "delegate",
      p_new_value: delegateStartNumber
    })
  ]);

  if (refereeResult.error) {
    return logAndThrow("initialize referee counter", refereeResult.error, {
      leagueId,
      startNumber: refereeStartNumber
    });
  }

  if (delegateResult.error) {
    return logAndThrow("initialize delegate counter", delegateResult.error, {
      leagueId,
      startNumber: delegateStartNumber
    });
  }
}

/**
 * Map assignment role to official type for mission order numbering
 * Used to determine which counter sequence to use
 * IMPORTANT: Use the ROLE assigned in the match, not the official's qualification category
 * Roles are in French: Arbitre roles = referee, Délégué roles = delegate
 */
export function mapAssignmentRoleToType(role: string): OfficialType {
  const trimmedRole = role.trim();

  // Referee roles contain "Arbitre"
  // Examples: "Arbitre Central", "Arbitre Assistant 1", "Arbitre Assistant 2"
  if (trimmedRole.includes('Arbitre')) {
    return 'referee';
  }

  // Delegate roles contain "Délégué" or "Observateur"
  // Examples: "Délégué Principal", "Délégué Adjoint", "Observateur"
  if (trimmedRole.includes('Délégué') || trimmedRole.includes('Observateur')) {
    return 'delegate';
  }

  // Default to referee if unknown role
  console.warn(`Unknown assignment role "${role}", defaulting to 'referee'`);
  return 'referee';
}

/**
 * @deprecated Use mapAssignmentRoleToType instead
 * Map official category to official type for mission order numbering
 * Categories are in French: Arbitre = referee, Délégué = delegate
 */
export function mapOfficialCategoryToType(category: string): OfficialType {
  console.warn('mapOfficialCategoryToType is deprecated. Use mapAssignmentRoleToType with assignment role instead.');
  const trimmedCategory = category.trim();

  // Referee categories start with "Arbitre"
  if (trimmedCategory.startsWith('Arbitre')) {
    return 'referee';
  }

  // Delegate categories start with "Délégué"
  if (trimmedCategory.startsWith('Délégué')) {
    return 'delegate';
  }

  // Default to referee if unknown category
  console.warn(`Unknown official category "${category}", defaulting to 'referee'`);
  return 'referee';
}
